/**
 * route-animator.js
 */

import React from 'react';
import { useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  useLocation,
  Switch,
} from "react-router-dom";
import {
  TransitionGroup,
  CSSTransition
} from "react-transition-group";
import Route from '../route';


const useStyles = makeStyles(theme => ({
  container: {
    perspective: 2000,
    height: '100vh',
    width: '100vw',
  },
  transitionContainer: {
    position: 'relative',
    minWidth: '100vw',
    minHeight: '100vh',
    backgroundColor: theme.palette.grey.colors[8],
    overflow: 'hidden',
    '& > *': {
      position: 'absolute',
      top: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: theme.palette.background.paper,
      backfaceVisibility: 'hidden',
      overflowY: 'scroll',
      overflowX: 'hidden',
    }
  }
}));


const useSlideForwardStyles = makeStyles(theme => ({
  enter: {
    transform: 'translateX(100%)'
  },
  enterActive: {
    transform: 'translateX(0%)',
    transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeInOut}`,
  },
  exit: {
    transform: 'translateX(0%)',
  },
  exitActive: {
    transform: 'translateX(-100%)',
    transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeInOut}`,
  }
}));


const useSlideBackStyles = makeStyles(theme => ({
  enter: {
    transform: 'translateX(-100%)'
  },
  enterActive: {
    transform: 'translateX(0%)',
    transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeInOut}`,
  },
  exit: {
    transform: 'translateX(0%)',
  },
  exitActive: {
    transform: 'translateX(100%)',
    transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeInOut}`,
  }
}));


const useFadeStyles = makeStyles(theme => ({
  enter: {
    opacity: 0
  },
  enterActive: {
    opacity: 1,
    transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeOut}`,
  },
  exit: {
    opacity: 1,
  },
  exitActive: {
    opacity: 0,
    transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeOut}`,
  }
}));


/**
 * NOTE: you should only use a single transition-wrapped instance at a time.
 * otherwise multiple transition animations will happen simultaneously.
 */
export default function AnimatedSwitch(props) {
  const {
    routes,
  } = props;
  const classes = useStyles();
  const slideForwardClasses = useSlideForwardStyles();
  const slideBackClasses = useSlideBackStyles();
  const fadeClasses = useFadeStyles();
  const theme = useTheme();
  let location = useLocation();
  let transitionClasses = null;
  const keyRef = React.useRef(location.pathname);


  // determine transition type from pIndex values
  // of current and previous location which were
  // previously set in location.state.transition object.
  // see utils/routing.js and/or routing/route-link
  if (location?.state?.transition?.pIndex !== undefined
    && location?.state?.prevLocation?.state?.transition?.pIndex !== undefined
  ) {
    const nextIndex = location.state.transition.pIndex;
    const currIndex = location.state.prevLocation.state.transition.pIndex;

    if (nextIndex !== currIndex && Math.floor(nextIndex) === Math.floor(currIndex)) {
      // fade
      transitionClasses = fadeClasses;
    }
    else {
      // slide
      if (nextIndex > currIndex) {
        transitionClasses = slideForwardClasses;
      } else if (nextIndex < currIndex) {
        transitionClasses = slideBackClasses;
      }
    }
  }

  // we only update the animation key if a transition has been determined to occur.
  // otherwise we re-use the previous key so that the route isn't completely re-rendered.
  const key = !transitionClasses
      ? keyRef.current
      : location.pathname;
  keyRef.current = key;


  return (
    <>
      <div className={classes.container}>
        <TransitionGroup
          className={classes.transitionContainer}
          childFactory={child => React.cloneElement(
            child,
            {
              classNames: transitionClasses,
              timeout: theme.transitions.duration.page + 100
            }
          )}
        >
          <CSSTransition
            key={key}
            timeout={theme.transitions.duration.page + 100}
          >
            <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Switch location={location}>
                {routes.map((route, i) =>
                  <Route
                    key={i}
                    {...route}
                  />)}
              </Switch>
            </div>
          </CSSTransition>
        </TransitionGroup>
      </div>
    </>
  );
}